/**
 * Nested dialog for editing an owner's sub-delegate seat limit
 * (`users.sub_delegate_limit`). Presentational — the caller owns the
 * mutation. Follows the `EditPermissionsDialog` nested-dialog pattern.
 *
 * Rules surfaced in the UI:
 *   - 0 is a valid limit (blocks all new invitations)
 *   - lowering below current usage never revokes existing delegates
 *   - `null` (reset) reverts to the platform default
 */

import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

type EditSeatLimitDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerName: string;
  /** Raw `users.sub_delegate_limit` column value (`null` = default applies). */
  currentLimit: number | null;
  /** Platform default used when no override is set. */
  defaultLimit: number;
  /** Active + pending seats currently in use. */
  usedCount: number;
  isSaving: boolean;
  errorMessage?: string | null;
  onSave: (limit: number | null) => void;
};

export function EditSeatLimitDialog({
  open,
  onOpenChange,
  ownerName,
  currentLimit,
  defaultLimit,
  usedCount,
  isSaving,
  errorMessage = null,
  onSave,
}: EditSeatLimitDialogProps) {
  const [value, setValue] = useState(String(currentLimit ?? defaultLimit));

  useEffect(() => {
    if (open) setValue(String(currentLimit ?? defaultLimit));
    // Re-sync only when the dialog (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parsedLimit = /^\d+$/.test(value.trim())
    ? Number.parseInt(value.trim(), 10)
    : null;
  const isValid = parsedLimit !== null;
  const isBelowUsage = isValid && parsedLimit < usedCount;

  const warningId = 'seat-limit-below-usage';
  const errorId = 'seat-limit-error';
  const describedBy =
    [isBelowUsage ? warningId : null, errorMessage ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-label={`Edit seat limit for ${ownerName}`}>
        <DialogHeader>
          <DialogTitle>Edit seat limit</DialogTitle>
          <DialogDescription>
            Maximum number of sub-delegate seats (active + pending) for{' '}
            {ownerName}. Set 0 to block new invitations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">
              Maximum sub-delegate seats
            </span>
            <input
              type="number"
              min={0}
              step={1}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={isSaving}
              aria-label="Maximum sub-delegate seats"
              aria-invalid={!isValid || Boolean(errorMessage)}
              aria-describedby={describedBy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#026857] focus:outline-none focus:ring-1 focus:ring-[#026857] disabled:bg-gray-100"
            />
          </label>

          <p className="text-xs text-gray-500">
            Currently using {usedCount}{' '}
            {usedCount === 1 ? 'seat' : 'seats'} ·{' '}
            {currentLimit === null
              ? `platform default (${defaultLimit}) applies`
              : `custom limit of ${currentLimit} set`}
          </p>

          {isBelowUsage && (
            <div
              id={warningId}
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              data-testid="limit-below-usage-warning"
            >
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                New limit is below current usage. Existing sub-delegates and
                pending invitations keep their access — only new invitations
                will be blocked until usage drops below the limit.
              </span>
            </div>
          )}

          {errorMessage && (
            <p id={errorId} className="text-xs text-red-600" role="alert">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <button
            type="button"
            onClick={() => onSave(null)}
            disabled={isSaving}
            className="rounded-md px-3 py-2 text-sm text-gray-500 underline-offset-2 transition-colors hover:text-gray-700 hover:underline disabled:opacity-50"
          >
            Reset to default ({defaultLimit})
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => isValid && onSave(parsedLimit)}
              disabled={isSaving || !isValid}
              className="rounded-md bg-[#026857] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#025246] disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save limit'}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
